// Pure profile-builder logic for creator onboarding (PIT-48, Story 4/9 of the
// Real Creator Accounts epic PIT-44).
//
// Everything here is a pure function over a {@link CreatorProfileDraft} or a
// {@link CreatorRow}: no React, no Supabase. The onboarding view owns
// persistence and the ProfileBuilder component owns the inputs, so validation,
// step mapping and the row↔draft translation stay unit-testable in isolation —
// and the route guard can reuse `isCreatorProfileComplete` without pulling in
// any UI.
//
// Field shapes come from the `creators` table in the baseline migration; the
// builder never invents columns.
import type { CreatorProfileDraft, CreatorRow, NewCreatorFields, Platform } from '../types';

/** Platforms a creator can publish on — mirrors the `Platform` union. */
export const PLATFORM_OPTIONS: readonly Platform[] = ['TikTok', 'Instagram', 'YouTube'];

/** Suggested niches, matching the vocabulary the seeded profiles use. */
export const NICHE_OPTIONS: readonly string[] = [
  'Fitness',
  'Beauty',
  'Food',
  'Tech',
  'Fashion',
  'Travel',
  'Gaming',
  'Home',
];

/** Preset avatars for the profile card; the creator may type any emoji. */
export const EMOJI_OPTIONS: readonly string[] = ['✦', '🏋️', '💄', '🍳', '💻', '👗', '✈️', '🎮'];

/** Avatar used when the creator doesn't pick one. */
export const DEFAULT_PROFILE_EMOJI = '✦';

/**
 * Card background stamped on every profile built here. `creators.bg` is
 * nullable and Discover renders the raw value with no fallback, so a new
 * profile without one would show a transparent thumbnail.
 */
export const DEFAULT_PROFILE_BG = '#1a1208';

export const MAX_TAGS = 6;
export const MIN_BIO_CHARS = 40;
export const MAX_BIO_CHARS = 600;
/** Upper bound on a per-deal rate, guarding against a stray extra digit. */
export const MAX_RATE = 100000;

/** Handles are stored with a leading `@`, e.g. `@jordanreeves`. */
const HANDLE_BODY = /^[A-Za-z0-9._]{3,30}$/;

/**
 * Handles nobody may claim: names the platform speaks with, or that would read
 * as official to a brand deciding who to trust (PIT-55).
 *
 * The enforcement point is the `creators_handle_not_reserved` check constraint
 * — an app-side list is bypassed by the direct REST calls the anon key allows.
 * This copy exists to say so before the creator submits, and **must be kept in
 * step with the migration**, which is the one that decides.
 */
export const RESERVED_HANDLES: readonly string[] = [
  'admin',
  'support',
  'pitchvault',
  'api',
  'help',
  'root',
  'official',
  'team',
];

/**
 * A save that failed because of one specific field, so the builder can put the
 * message back on the input that caused it rather than showing a bare toast.
 * Onboarding raises this when the database rejects a handle that passed every
 * check the client could make — the taken-handle race the unique index settles.
 */
export class ProfileFieldError extends Error {
  readonly field: keyof CreatorProfileDraft;

  constructor(field: keyof CreatorProfileDraft, message: string) {
    super(message);
    this.name = 'ProfileFieldError';
    this.field = field;
  }
}

/** The three stages of the builder, in order. */
export const PROFILE_BUILDER_STEPS = [
  {
    title: 'Identity',
    hint: 'How brands recognise you at a glance.',
  },
  {
    title: 'Focus',
    hint: 'What you make, and where you post it.',
  },
  {
    title: 'Offer',
    hint: 'What working with you costs, and why it is worth it.',
  },
] as const;

/** Index of a stage in {@link PROFILE_BUILDER_STEPS}. */
export type ProfileBuilderStep = 0 | 1 | 2;

/** Last stage — submitting it completes onboarding. */
export const LAST_STEP: ProfileBuilderStep = 2;

/**
 * Every stage, in order. The edit surface saves all of them at once, where
 * onboarding walks them one at a time.
 */
export const ALL_STEPS: readonly ProfileBuilderStep[] = [0, 1, 2];

/** A blank draft, used when the creator has no row to resume. */
export const EMPTY_DRAFT: CreatorProfileDraft = {
  name: '',
  handle: '',
  emoji: DEFAULT_PROFILE_EMOJI,
  avatarPath: null,
  niche: '',
  tags: [],
  platforms: [],
  portfolioPaths: [],
  rate: '',
  bio: '',
};

/** Errors for a step, keyed by the draft field that failed validation. */
export type DraftErrors = Partial<Record<keyof CreatorProfileDraft, string>>;

/**
 * Normalise a typed handle to storage form: exactly one leading `@`, no
 * internal whitespace. `@ jordan` and `jordan` both become `@jordan`.
 */
export function normalizeHandle(handle: string): string {
  const body = handle.replace(/\s+/g, '').replace(/^@+/, '');
  return body ? `@${body}` : '';
}

/** Parse the raw rate text into whole dollars, or null when unusable. */
export function parseRate(rate: string): number | null {
  // Accept what people actually type: "$1,800", "1800", " 1800 ".
  const digits = rate.replace(/[$,\s]/g, '');
  if (!/^\d+$/.test(digits)) return null;
  const value = Number(digits);
  return Number.isSafeInteger(value) ? value : null;
}

/** Format whole dollars as the `creators.rate` label, e.g. `"$1,800"`. */
export function formatRate(rate: number): string {
  return `$${rate.toLocaleString('en-US')}`;
}

/** Keep only the platform strings the app knows how to render. */
function knownPlatforms(values: readonly string[] | null | undefined): Platform[] {
  return (values ?? []).filter((v): v is Platform =>
    (PLATFORM_OPTIONS as readonly string[]).includes(v)
  );
}

/**
 * Seed the builder from a row the creator already owns, so an abandoned
 * onboarding resumes where it stopped instead of starting over. A null row
 * (nothing built yet) yields the blank draft.
 */
export function draftFromRow(row: CreatorRow | null): CreatorProfileDraft {
  if (!row) return { ...EMPTY_DRAFT, tags: [], platforms: [], portfolioPaths: [] };
  return {
    name: row.name ?? '',
    handle: row.handle ?? '',
    emoji: row.emoji || DEFAULT_PROFILE_EMOJI,
    avatarPath: row.avatar_path ?? null,
    niche: row.niche ?? '',
    tags: [...(row.tags ?? [])],
    platforms: knownPlatforms(row.platforms),
    portfolioPaths: [...(row.portfolio_paths ?? [])],
    rate: row.rate_num ? String(row.rate_num) : '',
    bio: row.bio ?? '',
  };
}

/**
 * Whether a row carries everything onboarding collects. Drives the two routing
 * decisions this story needs: a complete row means skip the builder and go
 * straight to the dashboard, an incomplete one means resume it.
 */
export function isCreatorProfileComplete(row: CreatorRow | null): boolean {
  if (!row) return false;
  return Object.keys(validateSteps(draftFromRow(row), ALL_STEPS)).length === 0;
}

/**
 * The stage a resumed profile should open on: the first one still missing
 * something, or the last stage when every field is already filled in.
 */
export function firstIncompleteStep(row: CreatorRow | null): ProfileBuilderStep {
  const draft = draftFromRow(row);
  return ALL_STEPS.find((step) => Object.keys(validateStep(step, draft)).length > 0) ?? LAST_STEP;
}

/**
 * The `creators` columns a given stage writes. Splitting the draft per stage is
 * what makes resumption work: each stage persists as it is completed, so a
 * creator who leaves after stage one still owns a (partial) row.
 *
 * Deliberately absent: `published`. New rows start unpublished via the column
 * default from PIT-45, and leaving it out means the same mapping can back the
 * edit surface — writing `published: false` here would quietly unpublish a live
 * profile every time its owner corrected a typo in their name.
 */
export function stepFields(step: ProfileBuilderStep, draft: CreatorProfileDraft): NewCreatorFields {
  if (step === 0) {
    return {
      name: draft.name.trim(),
      handle: normalizeHandle(draft.handle),
      emoji: draft.emoji || DEFAULT_PROFILE_EMOJI,
      avatar_path: draft.avatarPath,
      bg: DEFAULT_PROFILE_BG,
    };
  }
  if (step === 1) {
    return {
      niche: draft.niche.trim(),
      tags: draft.tags,
      platforms: draft.platforms,
      portfolio_paths: draft.portfolioPaths,
    };
  }
  const rate = parseRate(draft.rate);
  return {
    rate: rate === null ? null : formatRate(rate),
    rate_num: rate,
    bio: draft.bio.trim(),
  };
}

/**
 * The columns `steps` write, merged. Onboarding passes the single stage it just
 * completed; the edit surface passes every stage. Both persist through the same
 * mapping, so a field can never mean one thing on create and another on edit.
 */
export function draftFields(
  draft: CreatorProfileDraft,
  steps: readonly ProfileBuilderStep[]
): NewCreatorFields {
  return steps.reduce<NewCreatorFields>(
    (fields, step) => ({ ...fields, ...stepFields(step, draft) }),
    {}
  );
}

/** {@link validateStep} across several stages, merged into one error map. */
export function validateSteps(
  draft: CreatorProfileDraft,
  steps: readonly ProfileBuilderStep[]
): DraftErrors {
  return steps.reduce<DraftErrors>(
    (errors, step) => ({ ...errors, ...validateStep(step, draft) }),
    {}
  );
}

/**
 * Required-field validation for one stage. Handle *availability* is a separate
 * story — this only checks the shape of what was typed.
 */
export function validateStep(step: ProfileBuilderStep, draft: CreatorProfileDraft): DraftErrors {
  const errors: DraftErrors = {};

  if (step === 0) {
    if (draft.name.trim().length < 2) {
      errors.name = 'Tell brands what to call you.';
    }
    const handle = normalizeHandle(draft.handle);
    if (!handle) {
      errors.handle = 'Pick a handle so brands can find you.';
    } else if (!HANDLE_BODY.test(handle.slice(1))) {
      errors.handle = '3–30 characters, using letters, numbers, dots or underscores.';
    } else if (RESERVED_HANDLES.includes(handle.slice(1).toLowerCase())) {
      errors.handle = 'That handle is reserved — please choose another.';
    }
    return errors;
  }

  if (step === 1) {
    if (!draft.niche.trim()) {
      errors.niche = 'Choose the niche you are known for.';
    }
    if (draft.tags.length > MAX_TAGS) {
      errors.tags = `Keep it to ${MAX_TAGS} tags or fewer.`;
    }
    if (draft.platforms.length === 0) {
      errors.platforms = 'Select at least one platform you post on.';
    }
    return errors;
  }

  const rate = parseRate(draft.rate);
  if (rate === null || rate <= 0) {
    errors.rate = 'Enter your starting rate in whole dollars.';
  } else if (rate > MAX_RATE) {
    errors.rate = `That looks high — keep it under ${formatRate(MAX_RATE)}.`;
  }
  const bio = draft.bio.trim();
  if (bio.length < MIN_BIO_CHARS) {
    errors.bio = `A little more — at least ${MIN_BIO_CHARS} characters.`;
  } else if (bio.length > MAX_BIO_CHARS) {
    errors.bio = `Keep it under ${MAX_BIO_CHARS} characters.`;
  }
  return errors;
}
